import React, { useEffect, useState } from "react";
import { io } from "socket.io-client";
import DOMPurify from "dompurify";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { fetchBlogPostBySlug, clearSelectedPost } from "../redux/blogSlice";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  dropStaticDuplicates,
  ORGANIZATION_ID,
  ORGANIZATION_LOGO,
} from "../components/SEO/SEOHead";
import { COUNTRY_NAME, CURRENCY_LOCALE, SITE_ORIGIN } from "../config/market";

/* Public-facing web origin used for canonical URLs, OG/Twitter tags, and
   the publisher logo URL inside the Article JSON-LD. Read directly from
   REACT_APP_WEB_URL (set in Netlify build env) so it stays decoupled from
   REACT_APP_SERVER_URL — those are different origins now that the API is
   reached via api.lusuku.ug. The hardcoded fallback keeps deploys safe if
   the env var is ever cleared. Same pattern as netlify/edge-functions/og-inject.js. */
const WEB_URL = process.env.REACT_APP_WEB_URL || SITE_ORIGIN;

/**
 * BlogPosting structured data for one article. The publisher is the same
 * Lusuku the homepage describes (ORGANIZATION_ID, from SEOHead), so Google
 * files the article under Lusuku when someone searches the name. The logo is
 * logo512.png; the "/lusuku-logo.png" this used to name does not exist on the
 * site. A named author is a Person; the team byline is the organisation.
 */
export function articleJsonLd(post, { canonicalUrl, description, ogImage }) {
  const home = `${WEB_URL}/`;
  const byline = post.author || "Lusuku Team";
  const data = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description,
    url: canonicalUrl,
    mainEntityOfPage: canonicalUrl,
    image: ogImage,
    datePublished: post.publishedAt || post.createdAt,
    author: /lusuku/i.test(byline)
      ? { "@type": "Organization", name: byline, url: home }
      : { "@type": "Person", name: byline },
    publisher: {
      "@type": "Organization",
      "@id": ORGANIZATION_ID,
      name: "Lusuku",
      logo: { "@type": "ImageObject", url: ORGANIZATION_LOGO },
    },
    isPartOf: { "@type": "Blog", name: "Lusuku Blog", url: `${WEB_URL}/blog` },
  };
  if (post.updatedAt) data.dateModified = post.updatedAt;
  return data;
}

const CATEGORY_COLORS = {
  announcement: { bg: "#e3f2fd", text: "#1565c0" },
  strategy: { bg: "#fff3e0", text: "#e65100" },
  product: { bg: "#e8f5e9", text: "#2e7d32" },
  engineering: { bg: "#f3e5f5", text: "#6a1b9a" },
  policy: { bg: "#fce4ec", text: "#c62828" },
  other: { bg: "#f5f5f5", text: "#424242" },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString(CURRENCY_LOCALE, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const isHtmlContent = (text) => text && /^\s*<[a-zA-Z]/.test(text.trim());

const sanitizeHtml = (html) => {
  const clean = DOMPurify.sanitize(html, {
    ADD_TAGS: ["table", "thead", "tbody", "tr", "th", "td", "colgroup", "col", "mark", "span", "iframe"],
    ADD_ATTR: ["class", "style", "colspan", "rowspan", "data-color", "data-iframe-embed", "data-align", "src", "allowfullscreen", "allow", "frameborder", "width", "height"],
    FORCE_BODY: false,
  });
  return clean
    .replace(
      /<img([^>]*?)data-align="left"([^>]*?)>/g,
      '<div style="text-align:left"><img$1$2></div>'
    )
    .replace(
      /<img([^>]*?)data-align="center"([^>]*?)>/g,
      '<div style="text-align:center"><img$1$2></div>'
    )
    .replace(
      /<img([^>]*?)data-align="right"([^>]*?)>/g,
      '<div style="text-align:right"><img$1$2></div>'
    )
    .replace(
      /(<table[\s\S]*?<\/table>)/g,
      '<div class="table-scroll-wrapper">$1</div>'
    );
};

const renderContent = (content) => {
  if (!content) return null;
  if (isHtmlContent(content)) {
    return (
      <div
        className="blog-article-content"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
      />
    );
  }
  const lines = content.split("\n");
  const blocks = [];
  let i = 0;
  let listBuf = [];
  const flushList = () => {
    if (!listBuf.length) return;
    blocks.push(<ul key={`ul${i}`} style={{ paddingLeft: "1.5rem", margin: "0.5rem 0" }}>{listBuf.map((t, j) => <li key={j}>{t}</li>)}</ul>);
    listBuf = [];
  };
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("#### ")) { flushList(); blocks.push(<h4 key={i}>{line.slice(5)}</h4>); }
    else if (line.startsWith("### ")) { flushList(); blocks.push(<h3 key={i}>{line.slice(4)}</h3>); }
    else if (line.startsWith("## "))  { flushList(); blocks.push(<h2 key={i}>{line.slice(3)}</h2>); }
    else if (line.startsWith("# "))   { flushList(); blocks.push(<h1 key={i}>{line.slice(2)}</h1>); }
    else if (/^[•-] /.test(line))    { listBuf.push(line.replace(/^[•-] /, "")); }
    else if (line.trim() === "")      { flushList(); blocks.push(<br key={i} />); }
    else                              { flushList(); blocks.push(<p key={i} style={{ margin: "0.4rem 0" }}>{line}</p>); }
    i++;
  }
  flushList();
  return blocks;
};

const BlogArticlePage = () => {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const { selectedPost: post, isFetchingPost, error } = useSelector(
    (state) => state.blog
  );
  const [liveViewCount, setLiveViewCount] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    dispatch(fetchBlogPostBySlug(slug));
    return () => dispatch(clearSelectedPost());
  }, [dispatch, slug]);

  // A link shared before an article was renamed still resolves (the API
  // matches every slug the post was ever published under), but the address
  // bar should show where the article lives NOW, so the next person to copy
  // it shares the current link and the canonical tag below is right.
  useEffect(() => {
    if (post?.slug && post.slug !== slug) {
      history.replace(`/blog/${post.slug}`);
    }
  }, [post?.slug, slug, history]);

  useEffect(() => {
    if (!slug) return;
    const socketUrl = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_SOCKET_SERVER_URL || "http://localhost:8001";
    const socket = io(socketUrl, { transports: ["websocket", "polling"] });
    socket.on("blog:viewCount", (data) => {
      if (data.slug === slug) setLiveViewCount(data.viewCount);
    });
    return () => { socket.off("blog:viewCount"); socket.disconnect(); };
  }, [slug]);

  const colors = CATEGORY_COLORS[post?.category] || CATEGORY_COLORS.other;
  const canonicalUrl = `${WEB_URL}/blog/${slug}`;
  const ogImage = post?.ogImage || `${WEB_URL}/lusuku-og-default.png`;
  const description =
    post?.ogDescription ||
    post?.excerpt ||
    `Read the latest updates from Lusuku — ${COUNTRY_NAME}'s leading delivery marketplace.`;

  if (isFetchingPost) {
    return (
      <>
        <Navbar />
        <SkeletonPage>
          <SkeletonHero />
          <SkeletonBody>
            {[1, 2, 3, 4, 5].map((n) => (
              <SkeletonLine key={n} $width={n % 2 === 0 ? "75%" : "100%"} />
            ))}
          </SkeletonBody>
        </SkeletonPage>
        <Footer />
      </>
    );
  }

  if (error || (!isFetchingPost && !post)) {
    return (
      <>
        <Navbar />
        <NotFoundPage>
          <NotFoundIcon>📄</NotFoundIcon>
          <NotFoundTitle>Post not found</NotFoundTitle>
          <NotFoundText>
            This post may have been unpublished or the URL is incorrect.
          </NotFoundText>
          <BackButton onClick={() => history.push("/blog")}>
            ← Back to Insights
          </BackButton>
        </NotFoundPage>
        <Footer />
      </>
    );
  }

  return (
    <>
      {post && (
        /* dropStaticDuplicates: index.html ships its own description and
           og:url, and Helmet only manages the tags it wrote (see SEOHead). */
        <Helmet onChangeClientState={dropStaticDuplicates}>
          <title>{post.title} | Lusuku</title>
          <meta name="description" content={description} />

          {/* Open Graph — LinkedIn, Facebook, WhatsApp */}
          <meta property="og:title" content={post.title} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:site_name" content="Lusuku" />

          {/* Article specific OG */}
          <meta
            property="article:published_time"
            content={post.publishedAt || post.createdAt}
          />
          <meta property="article:author" content={post.author || "Lusuku Team"} />
          {post.tags?.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={post.title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={ogImage} />

          {/* Canonical */}
          <link rel="canonical" href={canonicalUrl} />

          {/* JSON-LD structured data for Google */}
          <script type="application/ld+json">
            {JSON.stringify(articleJsonLd(post, { canonicalUrl, description, ogImage }))}
          </script>
        </Helmet>
      )}

      <Navbar />

      <PageContainer>
        <ArticleHero $catBg={colors.bg}>
          <BackLink onClick={() => history.push("/blog")}>
            ← Insights & Announcements
          </BackLink>

          <CategoryTag $bg={colors.bg} $color={colors.text}>
            {post?.category || "post"}
          </CategoryTag>

          <ArticleTitle>{post?.title}</ArticleTitle>

          <ArticleMeta>
            <MetaItem>
              <MetaIcon>📅</MetaIcon>
              {formatDate(post?.publishedAt)}
            </MetaItem>
            <MetaSep />
            <MetaItem>
              <MetaIcon as="span" style={{ display: "inline-flex", alignItems: "center" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
              </MetaIcon>
              {post?.author || "Lusuku Team"}
            </MetaItem>
            {post?.version && (
              <>
                <MetaSep />
                <MetaItem>
                  <MetaIcon as="span" style={{ display: "inline-flex", alignItems: "center" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 15v4c0 1.1.9 2 2 2h14a2 2 0 002-2v-4"/><path d="M17 8l-5-5-5 5"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  </MetaIcon>
                  Version {post.version}
                </MetaItem>
              </>
            )}
            <MetaSep />
            <MetaItem>
              <MetaIcon as="span" style={{ display: "inline-flex", alignItems: "center" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              </MetaIcon>
              {(liveViewCount ?? post?.viewCount ?? 0).toLocaleString()} views
            </MetaItem>
          </ArticleMeta>

        </ArticleHero>

        <ArticleBody>
          {post?.excerpt && <LeadParagraph>{post.excerpt}</LeadParagraph>}

          <ArticleContent>{renderContent(post?.content)}</ArticleContent>

          {post?.tags?.length > 0 && (
            <TagsRow>
              {post.tags.map((tag) => (
                <Tag key={tag}>#{tag}</Tag>
              ))}
            </TagsRow>
          )}

          {(post?.preparedFor || post?.preparedBy) && (
            <PreparedRow>
              {post.preparedFor && (
                <PreparedItem>
                  <PreparedLabel>Prepared for</PreparedLabel>
                  <PreparedValue>{post.preparedFor}</PreparedValue>
                </PreparedItem>
              )}
              {post.preparedBy && (
                <PreparedItem>
                  <PreparedLabel>Prepared by</PreparedLabel>
                  <PreparedValue>{post.preparedBy}</PreparedValue>
                </PreparedItem>
              )}
            </PreparedRow>
          )}

          <ShareSection>
            <ShareTitle>Share this post</ShareTitle>
            <ShareButtons>
              <ShareButton
                $color="#0077B5"
                onClick={() =>
                  window.open(
                    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}`,
                    "_blank"
                  )
                }
              >
                <ShareIcon>in</ShareIcon>
                Share on LinkedIn
              </ShareButton>
              <ShareButton
                $color="#25D366"
                onClick={() =>
                  window.open(
                    `https://api.whatsapp.com/send?text=${encodeURIComponent(
                      `${post?.title} — ${canonicalUrl}`
                    )}`,
                    "_blank"
                  )
                }
              >
                <ShareIcon>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </ShareIcon>
                Share on WhatsApp
              </ShareButton>
              <ShareButton
                $color="#1877F2"
                onClick={() =>
                  window.open(
                    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl)}`,
                    "_blank"
                  )
                }
              >
                <ShareIcon>f</ShareIcon>
                Share on Facebook
              </ShareButton>
              <CopyButton
                onClick={() => {
                  navigator.clipboard.writeText(canonicalUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                $copied={copied}
              >
                <ShareIcon>{copied ? "✓" : "🔗"}</ShareIcon>
                {copied ? "Copied!" : "Copy link"}
              </CopyButton>
            </ShareButtons>
          </ShareSection>

          {post?.downloadUrl && (
            <DownloadCTA
              href={post.downloadUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              <DownloadCTAIcon>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </DownloadCTAIcon>
              <DownloadCTAText>
                <DownloadCTATitle>{post.downloadLabel || "Download this document"}</DownloadCTATitle>
                <DownloadCTASubtitle>Get the full PDF version of this article</DownloadCTASubtitle>
              </DownloadCTAText>
              <DownloadCTABadge>Free Download</DownloadCTABadge>
            </DownloadCTA>
          )}

          <BackButton onClick={() => history.push("/blog")}>
            ← Back to all posts
          </BackButton>
        </ArticleBody>
      </PageContainer>

      <Footer />
    </>
  );
};

export default BlogArticlePage;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #fafafa;
  padding-top: var(--lusuku-nav-h, 130px);
  width: 100%;
  overflow-x: hidden;
  @media (max-width: 768px) {
    padding-top: var(--lusuku-nav-h, 120px);
  }
`;

const ArticleHero = styled.div`
  background: ${(p) => p.$catBg || "#f5f5f5"};
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 3rem 1.5rem 2.5rem;
  width: 100%;
  overflow-x: hidden;
  @media (max-width: 768px) {
    padding: 1.5rem 1rem 1.5rem;
  }
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #1976d2;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-bottom: 1.5rem;
  display: block;
  &:hover { text-decoration: underline; }
`;

const CategoryTag = styled.span`
  display: inline-block;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 5px 12px;
  border-radius: 20px;
  border: 1.5px solid ${(p) => p.$color}44;
  margin-bottom: 1rem;
`;

const ArticleTitle = styled.h1`
  font-size: clamp(1.8rem, 3vw, 2.1rem);
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.3;
  max-width: 860px;
  margin: 0 auto 1.25rem;
  text-align: center;
  @media (max-width: 768px) {
    font-size: 1.45rem;
    text-align: left;
    margin: 0 0 1rem;
  }
`;

const ArticleMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  color: #666;
  margin-bottom: 1.5rem;
  @media (max-width: 768px) {
    justify-content: flex-start;
    font-size: 0.78rem;
    gap: 0.3rem 0.5rem;
    margin-bottom: 1rem;
  }
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const MetaIcon = styled.span`font-size: 0.85rem;`;

const MetaSep = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #ccc;
  display: inline-block;
`;

const PreparedRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  max-width: 600px;
  margin: 0 auto;
  @media (max-width: 768px) {
    gap: 1rem;
    padding: 0.75rem 1rem;
    justify-content: center;
    margin: 0 auto;
  }
`;

const PreparedItem = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PreparedLabel = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #888;
  margin-bottom: 0.25rem;
`;

const PreparedValue = styled.p`
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
`;

const ArticleBody = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 3rem 1.5rem 5rem;
  width: 100%;
  overflow-x: hidden;
  @media (max-width: 768px) {
    padding: 1.5rem 1rem 3rem;
  }
`;

const LeadParagraph = styled.p`
  font-size: 1.05rem;
  color: #444;
  line-height: 1.7;
  font-style: italic;
  border-left: 4px solid #1976d2;
  padding-left: 1.25rem;
  margin-bottom: 2.5rem;
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const ArticleContent = styled.div`
  font-size: 0.97rem;
  color: #333;
  line-height: 1.78;
  word-break: break-word;
  overflow-wrap: break-word;

  /* ── Headings ── */
  h1, h2, h3, h4, h5, h6 {
    color: #1a1a2e;
    font-weight: 700;
    margin: 2rem 0 0.75rem;
    line-height: 1.3;
  }
  h1 { font-size: 1.6rem; }
  h2 { font-size: 1.25rem; border-bottom: 2px solid #e3f2fd; padding-bottom: 0.4rem; }
  h3 { font-size: 1.08rem; }
  h4 { font-size: 1rem; }

  /* ── Paragraphs ── */
  p { margin: 0 0 1rem; }

  /* ── Text alignment ── */
  [style*="text-align: left"], [style*="text-align:left"] { text-align: left; }
  [style*="text-align: center"], [style*="text-align:center"] { text-align: center; }
  [style*="text-align: right"], [style*="text-align:right"] { text-align: right; }
  [style*="text-align: justify"], [style*="text-align:justify"] { text-align: justify; }

  /* ── Lists ── */
  ul, ol { padding-left: 1.75rem; margin: 0.5rem 0 1rem; }
  ul li { list-style: disc; margin: 0.3rem 0; }
  ol li { list-style: decimal; margin: 0.3rem 0; }

  /* ── Blockquote ── */
  blockquote {
    border-left: 4px solid #1976d2;
    margin: 1.5rem 0;
    padding: 0.5rem 1.25rem;
    color: #555;
    font-style: italic;
    background: #f5f9ff;
    border-radius: 0 6px 6px 0;
  }

  /* ── Images ── */
  img {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: inline-block;
    border-radius: 6px;
    margin: 0.75rem 0;
  }
  div[style*="text-align"] > img {
    display: inline-block;
  }
  div[style*="text-align"] {
    width: 100%;
    display: block;
    margin: 0.75rem 0;
  }

  /* ── Tables ── */
  div.table-scroll-wrapper {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 8px;
    box-shadow: 0 0 0 1px #d0d7de;
    margin: 1.5rem 0;
  }
  table {
    width: 100%;
    min-width: 400px;
    border-collapse: collapse;
    font-size: 0.92rem;
    display: table;
    table-layout: auto;
  }
  thead, thead tr:first-child th:first-child { border-top-left-radius: 8px; }
  thead tr:first-child th:last-child { border-top-right-radius: 8px; }
  thead { background: #f0f4f8; }
  th {
    background: #f0f4f8;
    font-weight: 700;
    color: #1a1a2e;
    padding: 0.65rem 1rem;
    border-bottom: 1px solid #d0d7de;
    border-right: 1px solid #d0d7de;
    text-align: left;
    white-space: nowrap;
  }
  th:last-child { border-right: none; }
  td {
    padding: 0.6rem 1rem;
    border-bottom: 1px solid #e8eaed;
    border-right: 1px solid #e8eaed;
    vertical-align: top;
  }
  td:last-child { border-right: none; }
  tr:last-child td { border-bottom: none; }
  tr:nth-child(even) td { background: #fafbfc; }
  tr:hover td { background: #f0f4f8; transition: background 0.15s; }
  /* Responsive: handled by div.table-scroll-wrapper */

  /* ── Iframes (YouTube / video embeds) ── */
  div[data-iframe-embed] {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    margin: 1.5rem 0;
    border-radius: 8px;
    background: #000;
  }
  div[data-iframe-embed] iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 8px;
  }

  /* ── Highlight & color marks ── */
  mark {
    border-radius: 3px;
    padding: 0 3px;
  }

  /* ── Links ── */
  a {
    color: #1976d2;
    text-decoration: underline;
  }
  a:hover { color: #1565c0; }

  /* ── Code ── */
  code {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.88em;
    font-family: 'Courier New', monospace;
  }
  pre {
    background: #1e1e2e;
    color: #cdd6f4;
    padding: 1.25rem;
    border-radius: 8px;
    overflow-x: auto;
    font-size: 0.88rem;
    margin: 1.5rem 0;
  }
  pre code { background: none; padding: 0; color: inherit; }

  /* ── Horizontal rule ── */
  hr {
    border: none;
    border-top: 2px solid #e0e0e0;
    margin: 2rem 0;
  }

  /* ── Responsive ── */
  @media (max-width: 768px) {
    font-size: 0.94rem;
    line-height: 1.7;
    h1 { font-size: 1.35rem; }
    h2 { font-size: 1.15rem; }
    h3 { font-size: 1rem; }
    table { font-size: 0.82rem; }
    th, td { padding: 0.4rem 0.6rem; }
    img { border-radius: 4px; max-width: 100%; }
    pre { font-size: 0.82rem; padding: 1rem; }
    blockquote { padding: 0.5rem 0.9rem; }
  }
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 2.5rem 0 1.5rem;
`;

const Tag = styled.span`
  background: #e3f2fd;
  color: #1565c0;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
`;

const ShareSection = styled.div`
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  padding: 2rem 0;
  margin: 2rem 0;
`;

const ShareTitle = styled.p`
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #888;
  margin-bottom: 1rem;
`;

const ShareButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.55rem 1.1rem;
  background: ${(p) => p.$color};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.88; }
  @media (max-width: 480px) {
    width: 100%;
    padding: 0.7rem 1rem;
  }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.55rem 1.1rem;
  background: ${(p) => p.$copied ? "#2e7d32" : "#f5f5f5"};
  color: ${(p) => p.$copied ? "#fff" : "#333"};
  border: 1px solid ${(p) => p.$copied ? "#2e7d32" : "#ddd"};
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  &:hover { background: ${(p) => p.$copied ? "#1b5e20" : "#e8e8e8"}; }
  @media (max-width: 480px) {
    width: 100%;
    padding: 0.7rem 1rem;
  }
`;

const ShareIcon = styled.span`
  font-size: 0.95rem;
  font-weight: 800;
`;

const BackButton = styled.button`
  display: inline-block;
  margin-top: 1.5rem;
  padding: 0.65rem 1.5rem;
  background: #1976d2;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #1565c0; }
  @media (max-width: 480px) {
    width: 100%;
    text-align: center;
    padding: 0.75rem 1rem;
  }
`;

const DownloadCTA = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #0168d0;
  color: white;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin: 1.5rem 0;
  text-decoration: none;
  transition: opacity 0.2s, transform 0.2s;
  &:hover { opacity: 0.93; transform: translateY(-1px); color: white; text-decoration: none; }
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem 1.1rem;
  }
`;

const DownloadCTAIcon = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  background: rgba(255,255,255,0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DownloadCTAText = styled.div`
  flex: 1;
`;

const DownloadCTATitle = styled.p`
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 0.2rem;
  color: white;
`;

const DownloadCTASubtitle = styled.p`
  font-size: 0.82rem;
  opacity: 0.82;
  margin: 0;
  color: white;
`;

const DownloadCTABadge = styled.span`
  flex-shrink: 0;
  background: rgba(255,255,255,0.2);
  border: 1.5px solid rgba(255,255,255,0.4);
  border-radius: 20px;
  padding: 0.3rem 0.9rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  @media (max-width: 600px) {
    align-self: flex-start;
  }
`;

const SkeletonPage = styled.div`
  padding-top: var(--lusuku-nav-h, 130px);
  min-height: 100vh;
  background: #fafafa;
`;

const SkeletonHero = styled.div`
  height: 280px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 800px 100%;
  animation: shimmer 1.4s ease infinite;
  @keyframes shimmer {
    0% { background-position: -800px 0; }
    100% { background-position: 800px 0; }
  }
`;

const SkeletonBody = styled.div`
  max-width: 780px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SkeletonLine = styled.div`
  height: 16px;
  width: ${(p) => p.$width || "100%"};
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 800px 100%;
  animation: shimmer 1.4s ease infinite;
  border-radius: 6px;
`;

const NotFoundPage = styled.div`
  padding-top: 200px;
  text-align: center;
  padding-bottom: 5rem;
`;

const NotFoundIcon = styled.div`font-size: 4rem; margin-bottom: 1rem;`;
const NotFoundTitle = styled.h2`font-size: 1.5rem; color: #333; margin-bottom: 0.5rem;`;
const NotFoundText = styled.p`color: #888; margin-bottom: 2rem;`;
